import React, { useContext } from 'react';
import { NewspaperOrder } from 'lib/types/newspaperOrder';
import { Alert } from 'lib/components/Alert';
import { capitalize } from 'lodash';
import { Product } from 'lib/enums';
import { PublishingMedium } from 'lib/enums/PublishingMedium';
import { Badge } from 'lib/components/Badge';
import SelectSchedule from './SelectSchedule';
import { NewspapersContext } from '../../contexts/NewspapersContext';
import { NewspaperOrderWithPublishingSettings } from './validation';
import { getNewspaperLogo } from '../../helpers/newspapers';

type PerPublisherSettingsProps = {
  product: Product;
  newspaperOrderWithPublishingSettings: NewspaperOrderWithPublishingSettings;
  onNewspaperOrderChange: (newspaperOrder: Partial<NewspaperOrder>) => void;
  disableEdits?: boolean;
};

function PerPublisherSettings({
  product,
  newspaperOrderWithPublishingSettings,
  onNewspaperOrderChange,
  disableEdits
}: PerPublisherSettingsProps) {
  const { newspaperOrder, publishingSettings, packagePublisherSetting } =
    newspaperOrderWithPublishingSettings;
  const { publishersAvailableForPlacement } = useContext(NewspapersContext);
  const newspaperSnap = publishersAvailableForPlacement.find(
    snap => snap.id === newspaperOrder.newspaper?.id
  );

  const { publishingSetting } = publishingSettings;

  if (!newspaperSnap) {
    return (
      <Alert
        id="missing-newspaper"
        title="Error"
        description="Missing newspaper"
      />
    );
  }
  if (!publishingSetting) {
    return (
      <Alert
        id="missing-publishing-setting"
        title="Error"
        description="Could not retrieve publishing setting"
      />
    );
  }

  const selectedFilingTypeId = newspaperOrder.filingType?.id;
  const filingType = publishingSettings.filingTypes.find(
    ft => ft.id === selectedFilingTypeId
  );
  const publicationDateSettings =
    packagePublisherSetting?.publicationDateSettings ||
    filingType?.modelData.publicationDateSettings;

  function handlePublicationDateChange(newPublishingDates: string[]) {
    onNewspaperOrderChange({
      ...newspaperOrder,
      publishingDates: newPublishingDates
    });
  }

  const logoPath = getNewspaperLogo(newspaperSnap);

  return (
    <div className="flex flex-col gap-y-3">
      <div>
        {logoPath && (
          <img
            className="h-12 max-w-48 object-contain rounded-lg"
            src={logoPath}
          />
        )}

        <h2 className="mt-3 text-lg text-column-gray-700 font-semibold">
          {newspaperSnap.data().name}
          <span className="ml-3">
            <Badge>
              {capitalize(
                newspaperOrder.publishingMedium || PublishingMedium.Print
              )}
            </Badge>
          </span>
        </h2>
      </div>

      <SelectSchedule
        newspaperOrder={newspaperOrder}
        newspaper={newspaperSnap}
        publicationDateSettings={publicationDateSettings}
        handlePublicationDateChange={handlePublicationDateChange}
        publishingSetting={publishingSetting}
        product={product}
        disableEdits={disableEdits}
      />
    </div>
  );
}

export default PerPublisherSettings;
